
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";

interface AgentCardProps {
  title: string;
  description: string;
  price: string;
  icon: string;
}

const AgentCard = ({ title, description, price, icon }: AgentCardProps) => {
  const { toast } = useToast();
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setImageError(true);
    setIsLoading(false);
  };

  // Optimize image URL with WebP and reduced quality
  const optimizedImageUrl = icon.includes('unsplash.com') 
    ? `${icon}&w=300&q=70&fm=webp` 
    : icon;

  return (
    <Card className="group hover:shadow-xl transition-all duration-500 border-2 hover:border-accent transform hover:-translate-y-2">
      <CardHeader>
        <div className="w-24 h-24 mb-4 mx-auto group-hover:scale-110 transition-transform duration-300">
          {isLoading && (
            <Skeleton className="w-24 h-24 rounded-full" />
          )}
          <Avatar className={`w-full h-full ${isLoading ? 'hidden' : ''}`}>
            <AvatarImage 
              src={optimizedImageUrl} 
              alt={`${title} - AI Agent Profile Picture`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="transition-all duration-300 group-hover:brightness-110" 
            />
            <AvatarFallback>{title[0]}</AvatarFallback>
          </Avatar>
        </div>
        <CardTitle className="text-center text-xl group-hover:text-primary transition-colors duration-300">
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-center text-gray-600 mb-4">
          {description}
        </p>
        <p className="text-center text-2xl font-bold text-primary">
          {price}
        </p>
        <div className="mt-4 space-y-2">
          {["24/7 Support", "Custom Integration", "Regular Updates"].map((feature, index) => (
            <div 
              key={feature}
              className="flex items-center justify-center gap-2"
            >
              <span className="text-green-500">✓</span>
              <span className="text-sm text-gray-600">
                {feature}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button 
          variant="default" 
          onClick={() => window.location.href = 'https://forms.gle/qQLw9WTSNqFkdt8E9'}
          className="w-full sm:w-auto"
        >
          Get Started
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AgentCard;
